export default function TaxLiensSection() {
  return (
    <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-gray-900" style={{ fontFamily: 'Poppins, sans-serif' }}>
            Sell Land with Back Taxes or Liens
          </h2>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
            Delinquent property taxes and liens don't have to mean losing your land. We buy properties with tax problems and can help you walk away with cash instead of facing foreclosure.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
          <div>
            <h3 className="text-3xl font-bold mb-6 text-gray-900">Understanding Property Tax Delinquency</h3>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              In Texas, property taxes are due by January 31st each year. If you don't pay, penalties and interest start accruing immediately - up to 12% in the first year alone. After several years of non-payment, the county can foreclose and sell your land at a tax sale for pennies on the dollar.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Many landowners fall behind on taxes for legitimate reasons: job loss, medical bills, divorce, or simply forgetting about land they inherited. The problem compounds quickly as penalties stack up year after year.
            </p>
            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
              Here's the critical point: <strong>you don't have to lose your land to a tax sale</strong>. Even if you owe thousands in back taxes, selling to a cash buyer like TRex RE LLC can help you avoid foreclosure and potentially walk away with money in your pocket.
            </p>
            <div className="bg-teal-50 border-l-4 border-teal-600 p-6 rounded-lg">
              <h4 className="font-bold text-gray-900 mb-2">Example Scenario:</h4>
              <p className="text-gray-700 leading-relaxed">
                You own 5 acres worth $40,000 but owe $8,000 in back taxes and penalties. Rather than losing the property to the county for $8,000, we can buy it for $35,000, pay off the $8,000 tax debt at closing, and you receive $27,000 cash. That's $27,000 more than you'd get from a tax foreclosure.
              </p>
            </div>
          </div>

          <div className="w-full h-96 rounded-lg overflow-hidden shadow-2xl">
            <img
              src="https://public.readdy.ai/ai/img_res/8cf1604ffa9b7d49a6c5b4230f3483c3.jpg"
              alt="Sell land with back taxes"
              className="w-full h-full object-cover"
            />
          </div>
        </div>

        <div className="mb-16">
          <h3 className="text-3xl font-bold mb-8 text-gray-900">How We Handle Tax Liens and Judgments</h3>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white rounded-lg p-8 shadow-lg">
              <div className="w-16 h-16 flex items-center justify-center bg-teal-100 rounded-lg mb-6">
                <i className="ri-search-eye-line text-teal-600 text-3xl"></i>
              </div>
              <h4 className="text-xl font-bold mb-4 text-gray-900">Title Search & Lien Discovery</h4>
              <p className="text-gray-700 leading-relaxed mb-4">
                We order a comprehensive title search to identify all liens, judgments, and encumbrances on your property. This includes property tax liens, IRS liens, mechanic's liens, HOA liens, and any court judgments.
              </p>
              <p className="text-gray-700 leading-relaxed">
                Many sellers don't even know what liens exist on their property. We uncover everything and provide you with a clear picture of what needs to be paid off.
              </p>
            </div>

            <div className="bg-white rounded-lg p-8 shadow-lg">
              <div className="w-16 h-16 flex items-center justify-center bg-teal-100 rounded-lg mb-6">
                <i className="ri-calculator-line text-teal-600 text-3xl"></i>
              </div>
              <h4 className="text-xl font-bold mb-4 text-gray-900">Equity Calculation</h4>
              <p className="text-gray-700 leading-relaxed mb-4">
                We determine your property's fair market value, subtract all liens and payoff amounts, and calculate your net equity. If there's equity remaining, we make you a cash offer based on that number.
              </p>
              <p className="text-gray-700 leading-relaxed">
                Even if liens consume most of the value, selling is still better than foreclosure. You avoid the credit damage and potential deficiency judgments that come with tax sales.
              </p>
            </div>

            <div className="bg-white rounded-lg p-8 shadow-lg">
              <div className="w-16 h-16 flex items-center justify-center bg-teal-100 rounded-lg mb-6">
                <i className="ri-hand-coin-line text-teal-600 text-3xl"></i>
              </div>
              <h4 className="text-xl font-bold mb-4 text-gray-900">Direct Payoff at Closing</h4>
              <p className="text-gray-700 leading-relaxed mb-4">
                At closing, our title company pays off all liens directly from the purchase proceeds. You don't have to negotiate with the county, IRS, or other creditors - we handle everything.
              </p>
              <p className="text-gray-700 leading-relaxed">
                This ensures clean title transfer and eliminates any future liability. You walk away free and clear with whatever equity remains.
              </p>
            </div>

            <div className="bg-white rounded-lg p-8 shadow-lg">
              <div className="w-16 h-16 flex items-center justify-center bg-teal-100 rounded-lg mb-6">
                <i className="ri-shield-check-line text-teal-600 text-3xl"></i>
              </div>
              <h4 className="text-xl font-bold mb-4 text-gray-900">Credit Protection</h4>
              <p className="text-gray-700 leading-relaxed mb-4">
                Selling before foreclosure protects your credit score. Tax foreclosures and judgments can devastate your credit for 7-10 years, making it difficult to buy a home, get loans, or even rent an apartment.
              </p>
              <p className="text-gray-700 leading-relaxed">
                By selling voluntarily, you avoid this credit damage and maintain control of the situation rather than having the county seize your property.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-red-600 to-red-700 rounded-lg p-8 text-white">
          <div className="flex items-start space-x-4">
            <div className="w-12 h-12 flex items-center justify-center flex-shrink-0">
              <i className="ri-alarm-warning-fill text-4xl"></i>
            </div>
            <div>
              <h3 className="text-2xl font-bold mb-3">Don't Wait Until It's Too Late</h3>
              <p className="text-red-50 leading-relaxed mb-4">
                Once the county files a tax foreclosure lawsuit, your options become limited. The best time to sell is before legal action begins. If you've received a tax delinquency notice or lawsuit, contact us immediately.
              </p>
              <p className="text-red-50 leading-relaxed mb-6">
                We've helped dozens of landowners avoid foreclosure by purchasing their properties quickly and paying off tax debts. Even if you think your situation is hopeless, call us - we may be able to help.
              </p>
              <a 
                href="tel:+1-915-206-5814" 
                className="inline-flex items-center space-x-2 px-6 py-3 bg-white text-red-600 rounded-lg font-bold hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-phone-fill"></i>
                <span>Get Help Now: (915) 206-5814</span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
